import { de, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';

import type { SupportedLocale } from './index';

/**
 * Locale-aware formatting for the things that are NOT copy: dates, relative times and numbers.
 *
 * A translated page still reading "3 minutes ago" and "1,234" is the half-done version of this
 * feature, and the half nobody notices in review because no catalogue key is missing.
 *
 * `en-US` rather than `en-GB` for English, matching the `en` catalogue's spelling and the app's
 * existing `dd MMM yyyy` patterns, which are unambiguous either way.
 */
const DATE_FNS_LOCALES: Record<SupportedLocale, Locale> = {
  de,
  en: enUS,
};

/** The `date-fns` locale for a `format` / `formatDistanceToNow` call. */
export function dateFnsLocale(locale: SupportedLocale): Locale {
  return DATE_FNS_LOCALES[locale];
}

/**
 * The BCP-47 tag for `Intl` and `toLocaleString`.
 *
 * Regionalised on purpose: bare `de` gives `1.234` and `07.09.2026` already, but bare `en` gives
 * a US-ambiguous `9/7/2026` in some engines and nothing in others — naming the region makes the
 * output the same everywhere the tool is opened.
 */
export function intlLocale(locale: SupportedLocale): string {
  return locale === 'de' ? 'de-DE' : 'en-GB';
}

/** A count, grouped the way the current locale groups thousands. */
export function formatCount(value: number, locale: SupportedLocale): string {
  return value.toLocaleString(intlLocale(locale));
}

/**
 * A decimal with one fractional digit — every rate and percentage in this tool.
 *
 * `toFixed(1)` was what these did before, which prints `12.3` to a German operator who reads
 * `12,3`. Fixed digits are kept, because a rate that changes width as it changes value makes a
 * column of them unreadable.
 */
export function formatRate(value: number, locale: SupportedLocale): string {
  return value.toLocaleString(intlLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/**
 * A BCP-47 tag as the name of the language, in the reader's own language.
 *
 * The ticket sidebar showed the merchant's raw `locale` — `de` next to the label "Sprache",
 * which is a tag, not an answer. `Intl.DisplayNames` covers every tag a merchant's Shopify
 * admin can be set to, so no catalogue entry has to be added per language we do not ship.
 *
 * Falls back to the tag itself: an engine without `DisplayNames`, or a value that is not a tag
 * at all, must still print something the operator can act on.
 */
export function formatLanguageName(
  tag: string | null | undefined,
  locale: SupportedLocale,
): string {
  if (!tag) return '—';
  try {
    const names = new Intl.DisplayNames([intlLocale(locale)], {
      type: 'language',
    });
    return names.of(tag) ?? tag;
  } catch {
    return tag;
  }
}
